import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Paper,
  Text,
  Title,
  Button,
  Group,
  Stack,
  Center,
  Loader,
  Alert,
  ThemeIcon,
  ActionIcon,
} from "@mantine/core";
import { motion, AnimatePresence } from "framer-motion";
import {
  IconCheck,
  IconX,
  IconMail,
  IconAlertCircle,
  IconRefresh,
  IconArrowRight,
  IconHome,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  Colors,
  HttpErrorCodesObj,
  HttpSuccessCodesObj,
  ContactInformation,
} from "../../Lib/data";
import { AllFatherContext } from "../../App";
import { Endpoints } from "../../Lib/endpoints";
import { DefaultErrorNotification, DefaultSuccessNotification } from "../../Lib/methods";
import { NonPaginatedResponse } from "../../Lib/responses";
import { getSiteName } from "../../Lib/siteContent";
import { PerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";

interface VerificationResponse {
  message: string;
}

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const context = useContext(AllFatherContext);
  const siteName = getSiteName(context?.assets);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<"idle" | "success" | "error">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const tokenParam = searchParams.get("t");
    if (tokenParam) {
      setToken(tokenParam);
      verifyEmail(tokenParam);
    } else {
      setVerificationStatus("error");
      setErrorMessage("No verification token found in URL");
    }
  }, [searchParams]);

  const verifyEmail = async (verificationToken: string) => {
    setIsVerifying(true);
    setVerificationStatus("idle");

    try {
      const r = await PerformRequest<NonPaginatedResponse<VerificationResponse>>({
        method: Endpoints.GetVerifyEmail(verificationToken).method,
        route: Endpoints.GetVerifyEmail(verificationToken).route,
        data: {
          token: verificationToken,
        },
        callback: () => {
          setIsVerifying(false);
        },
      });

      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          setVerificationStatus("success");
          DefaultSuccessNotification("Email verified successfully!");
        } else if (r.status === HttpErrorCodesObj.BadRequest) {
          // STUDENT ALREADY VERIFIED
          setVerificationStatus("success");
          DefaultSuccessNotification("Email already verified!");
        } else if (r.status === HttpErrorCodesObj.NotFound) {
          setVerificationStatus("error");
          setErrorMessage("Verification token not found");
        } else if (r.status === HttpErrorCodesObj.Expired) {
          setVerificationStatus("error");
          setErrorMessage("Verification token expired");
        } else {
          setVerificationStatus("error");
          setErrorMessage(r.message || "Verification failed");
        }
      } else {
        setVerificationStatus("error");
        setErrorMessage("Network error. Please try again.");
      }
    } catch (error) {
      setVerificationStatus("error");
      setErrorMessage("An unexpected error occurred");
    }

    setIsVerifying(false);
  };

  const handleRetry = () => {
    if (token) {
      verifyEmail(token);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <div className="verify-email-page">
      {/* Background Elements */}
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <Container size="sm" className="verify-email-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="verify-email-content"
        >
          {/* Logo Section */}
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="logo-section"
          >
            <img src={context?.assets?.logos?.legacy} alt={siteName} className="logo" />
            <Title order={1} className="app-title">
              {siteName}
            </Title>
          </motion.div>

          {/* Verification Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Paper className="verification-card" radius="lg" p="xl" withBorder>
              <Stack gap="xl" align="center">
                {/* Status Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <AnimatePresence mode="wait">
                    {isVerifying ? (
                      <motion.div
                        key="loading"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ThemeIcon variant="light" color="blue" size={80} radius={40}>
                          <Loader size={40} />
                        </ThemeIcon>
                      </motion.div>
                    ) : verificationStatus === "success" ? (
                      <motion.div
                        key="success"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ThemeIcon variant="light" color="green" size={80} radius={40}>
                          <IconCheck size={40} />
                        </ThemeIcon>
                      </motion.div>
                    ) : verificationStatus === "error" ? (
                      <motion.div
                        key="error"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ThemeIcon variant="light" color="red" size={80} radius={40}>
                          <IconX size={40} />
                        </ThemeIcon>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="idle"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ThemeIcon variant="light" color="gray" size={80} radius={40}>
                          <IconMail size={40} />
                        </ThemeIcon>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Status Title */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  style={{ textAlign: "center" }}
                >
                  <AnimatePresence mode="wait">
                    {isVerifying ? (
                      <motion.div
                        key="verifying"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Title order={2} mb="xs" className="status-title">
                          Verifying Your Email
                        </Title>
                        <Text size="lg" c="dimmed">
                          Please wait while we verify your email address...
                        </Text>
                      </motion.div>
                    ) : verificationStatus === "success" ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Title order={2} mb="xs" className="status-title" c="green">
                          Email Verified Successfully!
                        </Title>
                        <Text size="lg" c="dimmed">
                          Your email has been verified. You can now log in to your account.
                        </Text>
                      </motion.div>
                    ) : verificationStatus === "error" ? (
                      <motion.div
                        key="error"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Title order={2} mb="xs" className="status-title" c="red">
                          Verification Failed
                        </Title>
                        <Text size="lg" c="dimmed">
                          {errorMessage}
                        </Text>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="idle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Title order={2} mb="xs" className="status-title">
                          Email Verification
                        </Title>
                        <Text size="lg" c="dimmed">
                          Please wait while we process your verification...
                        </Text>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <AnimatePresence mode="wait">
                    {verificationStatus === "success" ? (
                      <motion.div
                        key="success-actions"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Group gap="md" justify="center">
                          <Button
                            size="md"
                            radius="md"
                            onClick={handleGoToLogin}
                            rightSection={<IconArrowRight size={16} />}
                            className="success-button"
                          >
                            Go to Login
                          </Button>
                          <Button
                            variant="light"
                            size="md"
                            radius="md"
                            onClick={handleGoHome}
                            leftSection={<IconHome size={16} />}
                          >
                            Go Home
                          </Button>
                        </Group>
                      </motion.div>
                    ) : verificationStatus === "error" ? (
                      <motion.div
                        key="error-actions"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Stack gap="md" align="center">
                          <Button
                            size="md"
                            radius="md"
                            onClick={handleRetry}
                            leftSection={<IconRefresh size={16} />}
                            className="retry-button"
                          >
                            Try Again
                          </Button>
                          <Group gap="md">
                            <Button variant="light" size="md" radius="md" onClick={handleGoToLogin}>
                              Go to Login
                            </Button>
                            <Button
                              variant="light"
                              size="md"
                              radius="md"
                              onClick={handleGoHome}
                              leftSection={<IconHome size={16} />}
                            >
                              Go Home
                            </Button>
                          </Group>
                        </Stack>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </motion.div>

                {/* Contact Support */}
                {verificationStatus === "error" && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <Alert
                      icon={<IconAlertCircle size="1rem" />}
                      title="Need Help?"
                      color="blue"
                      variant="light"
                    >
                      <Text size="sm" mb="xs">
                        If you're having trouble verifying your email, please contact our support
                        team.
                      </Text>
                      <Group gap="xs">
                        <IconMail size={14} />
                        <Text size="sm" fw={500}>
                          {ContactInformation.email}
                        </Text>
                      </Group>
                    </Alert>
                  </motion.div>
                )}
              </Stack>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </div>
  );
};

export default VerifyEmail;
